import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Typography, TextField, Button, IconButton, useMediaQuery } from '@mui/material';
import { transactionAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DetailsPanel from '../../components/ui/DetailsPanel';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import CustomSnackbar from '../../components/ui/Snackbar';
import { formatAmountForDisplay } from '../../utils/amountFormatter';
import editIcon from '../../assets/icons/edit.svg';

const TransactionDetails = ({ onEdit, refreshKey }) => {
  const { id } = useParams();
  const history = useHistory();
  const { user } = useAuth();
  const [transaction, setTransaction] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editText, setEditText] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({ open: false, commentId: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const stickyHeaderRef = useRef(null);
  const isMobile = useMediaQuery('(max-width:768px)');

  // Check if current user is the comment author
  const isCommentAuthor = (comment) => {
    return user && comment.author && user.id === comment.author.id;
  };

  // Fetch transaction details
  useEffect(() => {
    const fetchTransaction = async () => {
      try {
        setLoading(true);
        const response = await transactionAPI.getById(id);
        setTransaction(response.data);
        
        // Fetch comments for this transaction
        try {
          const commentsResponse = await transactionAPI.getComments(id);
          setComments(commentsResponse.data.content || []);
        } catch (commentError) {
          console.error('Error fetching comments:', commentError);
          setComments([]);
        }
      } catch (error) {
        console.error('Error fetching transaction:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTransaction();
    }
  }, [id, refreshKey]);

  const handleClose = () => {
    history.goBack();
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      const response = await transactionAPI.addComment(id, { body: newComment });
      setComments(prev => [response.data, ...prev]);
      setNewComment('');
      
      // Scroll to top to show the new comment
      setTimeout(() => {
        if (stickyHeaderRef.current) {
          // Find the scrolling container by traversing up the DOM
          let element = stickyHeaderRef.current.parentElement;
          while (element && element !== document.body) {
            const style = window.getComputedStyle(element);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
              element.scrollTo({ top: 0, behavior: 'smooth' });
              return;
            }
            element = element.parentElement;
          }
        }
      }, 100);
    } catch (error) {
      console.error('Error adding comment:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de l\'ajout du commentaire',
        severity: 'error'
      });
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editText.trim()) return;

    // Find the comment to check authorization
    const comment = comments.find(c => c.id === commentId);
    if (!isCommentAuthor(comment)) {
      console.error('Unauthorized: Only comment author can edit');
      return;
    }

    try {
      await transactionAPI.updateComment(id, commentId, { body: editText });
      setComments(prev => prev.map(comment => 
        comment.id === commentId ? { ...comment, body: editText } : comment
      ));
      setEditingComment(null);
      setEditText('');
    } catch (error) {
      console.error('Error updating comment:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la modification du commentaire',
        severity: 'error'
      });
    }
  };

  const handleDeleteComment = async (commentId) => {
    // Find the comment to check authorization
    const comment = comments.find(c => c.id === commentId);
    if (!isCommentAuthor(comment)) {
      console.error('Unauthorized: Only comment author can delete');
      return;
    }

    // Show confirmation dialog
    setDeleteDialog({ open: true, commentId });
  };

  const confirmDeleteComment = async () => {
    if (!deleteDialog.commentId) return;

    try {
      await transactionAPI.deleteComment(id, deleteDialog.commentId);
      setComments(prev => prev.filter(comment => comment.id !== deleteDialog.commentId));
      setDeleteDialog({ open: false, commentId: null });
    } catch (error) {
      console.error('Error deleting comment:', error);
      setSnackbar({
        open: true,
        message: 'Erreur lors de la suppression du commentaire',
        severity: 'error'
      });
    }
  };

  const startEditing = (comment) => {
    if (!isCommentAuthor(comment)) {
      console.error('Unauthorized: Only comment author can edit');
      return;
    }
    setEditingComment(comment.id);
    setEditText(comment.body);
  };

  const cancelEditing = () => {
    setEditingComment(null);
    setEditText('');
  };

  const getAmountDisplayAndColor = (transaction) => {
    // Use the exact same logic as transaction cards
    // Factors are stored directly on TransactionDTO at creation time
    const caisseFactor = transaction.caisseFactor !== undefined ? transaction.caisseFactor : 0;
    const coffreFactor = transaction.coffreFactor !== undefined ? transaction.coffreFactor : 0;
    const baseAmount = Number(transaction.amount) || 0;
    let amountDisplay = '';
    let amountColor = '#919191'; // Default gray
        
        // Check if it's neutral: both factors are 0, OR both factors are non-zero (mixed)
        const isNeutral = (caisseFactor === 0 && coffreFactor === 0);
        const isMixed = (caisseFactor !== 0 && coffreFactor !== 0);
        
        if (isNeutral) {
          // Neutral - show raw amount, blue color
          amountDisplay = formatAmountForDisplay(baseAmount, false).text;
          amountColor = '#027BC6';
        } else if (isMixed) {
          // Mixed (e.g. prélèvement) - show as negative, red color
          const adjustedAmount = -Math.abs(baseAmount);
          amountDisplay = formatAmountForDisplay(adjustedAmount, true).text;
          amountColor = '#EF456E';
        } else {
          // Pure positive or negative
          const factor = caisseFactor !== 0 ? caisseFactor : coffreFactor;
          const isPositive = factor > 0;
          const adjustedAmount = baseAmount * factor;
          amountDisplay = formatAmountForDisplay(adjustedAmount, true).text;
          amountColor = isPositive ? '#0AD69F' : '#FF6B6B';
        }
    
    return { amountDisplay, amountColor };
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} - ${hours}:${minutes}`;
  };

  const formatCommentDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  const overlaySx = {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100vh',
    backgroundColor: '#FFFFFF',
    boxShadow: '0px 20px 80px #00000011',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...(isMobile ? { width: '100%', minWidth: 0 } : { width: '25%', minWidth: '600px' }),
  };

  if (loading) {
    return (
      <Box sx={overlaySx}>
        <Typography>Chargement...</Typography>
      </Box>
    );
  }

  if (!transaction) {
    return (
      <Box sx={overlaySx}>
        <Typography>Transaction non trouvée</Typography>
      </Box>
    );
  }

  return (
    <DetailsPanel onClose={handleClose}>
      {/* Sticky Header Section */}
      <Box 
        ref={stickyHeaderRef}
        sx={{ 
          position: 'sticky',
          top: '-20px',
          backgroundColor: '#F9FAFC',
          zIndex: 10,
          marginLeft: '-20px',
          marginRight: '-20px',
          paddingLeft: '20px',
          paddingRight: '20px',
          paddingTop: '20px',
          paddingBottom: '20px',
          marginBottom: '20px'
        }}>
        {/* Transaction Header */}
      <Box sx={{ marginBottom: '20px' }}>
        {/* Top Row: Transaction Code (left) and Amount (right) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2px' }}>
          {/* Transaction Code - Top Left (smallest) */}
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 500,
              color: '#445E76',
              textTransform: 'none',
              opacity: 0.88
            }}
          >
            {transaction.code}
          </Typography>
          
          {/* Amount - Top Right */}
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '18px',
              fontWeight: 600,
              color: getAmountDisplayAndColor(transaction).amountColor,
              textTransform: 'none'
            }}
          >
            {getAmountDisplayAndColor(transaction).amountDisplay}
          </Typography>
        </Box>

        {/* Bottom Row: Category (left) and Date/Time (right) */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            {/* Category - directly under code */}
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '22px',
                fontWeight: 600,
                color: '#027BC6',
                textTransform: 'none',
                marginBottom: '2px',
                marginTop: '-10px'
              }}
            >
              {transaction.category?.label || 'N/A'}
            </Typography>
            
            {/* Caisse • Agent */}
            <Typography
              sx={{
                fontFamily: 'Montserrat',
                fontSize: '14px',
                fontWeight: 600,
                color: '#000000',
                textTransform: 'none'
              }}
            >
              {transaction.caisse?.name || 'N/A'} • {transaction.createdBy?.firstName} {transaction.createdBy?.lastName}
            </Typography>
          </Box>
          
          {/* Date & Time - Bottom Right (smaller) */}
          <Typography
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '13px',
              fontWeight: 600,
              color: '#9A9A9A',
              textTransform: 'none'
            }}
          >
            {formatDate(transaction.createdAt)}
          </Typography>
        </Box>
      </Box>

      {/* Description Section */}
      <Box sx={{ marginBottom: '20px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 500,
            color: '#445E76',
            textTransform: 'none',
              opacity: 0.88
          }}
        >
          Description :
        </Typography>
          {onEdit && transaction && user?.role === 'AGENT' && (
            <IconButton
              onClick={() => onEdit(transaction)}
              onTouchEnd={(e) => { e.preventDefault(); onEdit(transaction); }}
              sx={{
                padding: '4px',
                width: '20px',
                height: '20px',
                touchAction: 'manipulation',
                '&:hover': {
                  opacity: 0.7
                }
              }}
            >
              <img 
                src={editIcon} 
                alt="Modifier" 
                style={{ width: '12px', height: '12px' }}
              />
            </IconButton>
          )}
        </Box>
        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontSize: '15px',
            fontWeight: 600,
            color: '#2F2F2F',
            lineHeight: '22px',
            wordBreak: 'break-word',
            overflowWrap: 'break-word',
            hyphens: 'auto'
          }}
        >
          {transaction.description || 'Aucune description'}
        </Typography>
      </Box>

        <Typography
          sx={{
            fontFamily: 'Montserrat',
            fontSize: '20px',
            fontWeight: 600,
            color: '#75808E',
            textTransform: 'none',
            marginBottom: '20px'
          }}
        >
          Commentaires ({comments.length})
        </Typography>
      </Box>

      {/* Comments Section */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        flex: 1,
        minHeight: 0
      }}>
        {/* Comments List - Scrollable */}
        <Box>
          {comments.map((comment) => (
            <Box key={comment.id} sx={{ marginBottom: '20px' }}>
              {/* Comment Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#38485C',
                    textTransform: 'none'
                  }}
                >
                  {comment.author?.firstName} {comment.author?.lastName}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Montserrat',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: '#445E76',
                    textTransform: 'none',
                    opacity: 0.88,
                    textAlign: 'right',
                    maxWidth: 'calc(100% - 20px)'
                  }}
                >
                  {formatCommentDate(comment.createdAt)}
                </Typography>
              </Box>

              {/* Comment Content */}
              <Box
                sx={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E3E3E374',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '8px'
                }}
              >
                {editingComment === comment.id ? (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& fieldset': { border: 'none' }
                        },
                        marginBottom: '12px'
                      }}
                    />
                    {isCommentAuthor(comment) && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <Typography
                          onClick={cancelEditing}
                          onTouchEnd={(e) => { e.preventDefault(); cancelEditing(); }}
                          sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#9A9A9A',
                            textTransform: 'none',
                            cursor: 'pointer',
                            touchAction: 'manipulation',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        >
                          Annuler
                        </Typography>
                        <Typography
                          onClick={() => handleEditComment(comment.id)}
                          onTouchEnd={(e) => { e.preventDefault(); handleEditComment(comment.id); }}
                          sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#027BC6',
                            textTransform: 'none',
                            cursor: 'pointer',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        >
                          Sauvegarder
                        </Typography>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Typography
                      sx={{
                        fontFamily: 'Montserrat',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#707070',
                        textTransform: 'none',
                        opacity: 0.88,
                        lineHeight: '22px',
                        marginBottom: '12px'
                      }}
                    >
                      {comment.body}
                    </Typography>
                    {isCommentAuthor(comment) && (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
                        <Typography
                          onClick={() => startEditing(comment)}
                          onTouchEnd={(e) => { e.preventDefault(); startEditing(comment); }}
                          sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#027BC6',
                            textTransform: 'none',
                            cursor: 'pointer',
                            touchAction: 'manipulation',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        >
                          Modifier
                        </Typography>
                        <Typography
                          onClick={() => handleDeleteComment(comment.id)}
                          onTouchEnd={(e) => { e.preventDefault(); handleDeleteComment(comment.id); }}
                          sx={{
                            fontFamily: 'Montserrat',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#EF456E',
                            textTransform: 'none',
                            cursor: 'pointer',
                            touchAction: 'manipulation',
                            '&:hover': {
                              opacity: 0.8
                            }
                          }}
                        >
                          Supprimer
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Add Comment Section - Sticky at bottom */}
      <Box sx={{ 
        position: 'sticky',
        bottom: '-20px',
        backgroundColor: '#F9FAFC',
        paddingTop: '20px',
        paddingBottom: '20px',
        marginTop: 'auto',
        marginLeft: '-20px',
        marginRight: '-20px',
        paddingLeft: '20px',
        paddingRight: '20px',
        flexShrink: 0,
        zIndex: 10
      }}>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            border: '1px solid #E3E3E3',
            borderRadius: '12px',
            backgroundColor: '#FFFFFF',
            '&:hover': {
              borderColor: '#E3E3E3'
            },
            '&:focus-within': {
              borderColor: '#007BC6'
            }
          }}
        >
          <textarea
            placeholder="Ajouter un commentaire..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            style={{
              width: '100%',
              minHeight: '96px',
              padding: '12px 90px 12px 12px',
              border: 'none',
              outline: 'none',
              borderRadius: '12px',
              fontFamily: 'Montserrat',
              fontSize: '16px',
              fontWeight: 600,
              color: '#000000',
              backgroundColor: 'transparent',
              resize: 'none',
              boxSizing: 'border-box'
            }}
          />
        <Button
          onClick={handleAddComment}
          onTouchEnd={(e) => { e.preventDefault(); if (newComment.trim()) handleAddComment(); }}
          disabled={!newComment.trim()}
          sx={{
            position: 'absolute',
            bottom: '8px',
            right: '8px',
            backgroundColor: '#007BC6',
            color: '#FFFFFF',
            fontFamily: 'Montserrat',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: '12px',
            padding: '8px 16px',
            minWidth: '82px',
            height: '32px',
            '&:hover': {
              backgroundColor: '#005a9e'
            },
            '&:disabled': {
              backgroundColor: '#E3E3E3',
              color: '#9A9A9A'
            }
          }}
        >
          Ajouter
        </Button>
        </Box>
      </Box>

      {/* Delete Comment Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, commentId: null })}
        onConfirm={confirmDeleteComment}
        type="delete"
        title="Supprimer le commentaire"
        message="Êtes-vous sûr de vouloir supprimer ce commentaire ?"
        confirmText="Supprimer"
        cancelText="Annuler"
        loading={false}
      />

      {/* Error Snackbar */}
      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </DetailsPanel>
  );
};

export default TransactionDetails;

